import { useMemo, useState } from "react";
import { Navigate, useParams } from "react-router-dom";
import { SearchInput } from "@/core/components/search-input";
import { Stack } from "@/core/components/ui/stack";
import { useGetBusinessReviewAccess } from "@/business/query";
import {
  HeaderStack,
  MainCard,
  MainStack,
} from "@/business-settings/common/business-settings-page";
import { PageHeader } from "@/business-settings/common/page-header";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import { dayjs } from "@/core/utils/date";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import { useGetBusinessReviews } from "@/reviews/query";
import type { ReviewWithSearchableFields } from "@/reviews/review";
import { ReviewsTable } from "./reviews-table";
import { REVIEW_SEARCH_FIELDS } from "./search-constants";

const ReviewsContent = () => {
  const { businessId } = useParams();
  const { data: reviews = [], isLoading } = useGetBusinessReviews(businessId);
  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();
  const [searchValue, setSearchValue] = useState("");

  const sortedFilteredReviews = useMemo(() => {
    let filtered: ReviewWithSearchableFields[] = reviews;

    // Apply search filter using multi-field search
    if (searchValue) {
      filtered = multiFieldSearch(
        filtered,
        searchValue,
        REVIEW_SEARCH_FIELDS,
      ) as ReviewWithSearchableFields[];
    }

    // Sort by date, most recent first
    return filtered.sort((a, b) => {
      const dateA = a.external_created_at || a.created_at;
      const dateB = b.external_created_at || b.created_at;
      return dayjs(dateB).valueOf() - dayjs(dateA).valueOf();
    });
  }, [reviews, searchValue]);

  const handleRowClick = (review: ReviewWithSearchableFields) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.reviews,
      mode: SIDEBAR_MODES.view,
      selectedItemId: review.id,
      isOpen: true,
    });
  };

  return (
    <Stack className="mr-5" gap="sm" fullWidth>
      <HeaderStack>
        <PageHeader>Five-star reviews</PageHeader>
      </HeaderStack>
      <MainCard>
        <MainStack className="pt-6">
          <SearchInput
            placeholder="Search five-star reviews"
            value={searchValue}
            onChange={setSearchValue}
          />

          <div className="w-full overflow-auto">
            <ReviewsTable
              data={sortedFilteredReviews}
              isLoading={isLoading}
              selectedRowId={selectedItemId}
              onRowClick={handleRowClick}
            />
          </div>
        </MainStack>
      </MainCard>
    </Stack>
  );
};

const Reviews = () => {
  const { businessId } = useParams();
  const { data: reviewAccessData, isLoading } =
    useGetBusinessReviewAccess(businessId);

  if (isLoading) {
    return null;
  }

  if (!reviewAccessData?.has_review_access) {
    return <Navigate to="../" replace />;
  }

  return <ReviewsContent />;
};

export default Reviews;
